import type { EventBus } from './eventBus';
import type { WeaponDefinition } from '../weapons/types';
import { weaponRegistry } from '../weapons/registry';

export interface WeaponSelectionCallbacks {
  onWeaponChanged: (
    weapon: WeaponDefinition | null,
    previousWeapon: WeaponDefinition | null,
  ) => void;
  onRequiresTargeting: (weapon: WeaponDefinition) => void;
  playSelectSound: () => void;
  getCurrentWormInfo: () => { wormId: string; teamId: string } | null;
}

export class WeaponSelectionManager {
  private selectedWeapon: WeaponDefinition | null = null;
  private eventBus: EventBus | null = null;
  private callbacks: WeaponSelectionCallbacks | null = null;

  constructor(eventBus?: EventBus) {
    this.eventBus = eventBus ?? null;
  }

  setEventBus(eventBus: EventBus): void {
    this.eventBus = eventBus;
  }

  setCallbacks(callbacks: WeaponSelectionCallbacks): void {
    this.callbacks = callbacks;
  }

  select(weaponId: string): void {
    const previousWeapon = this.selectedWeapon;
    const newWeapon = weaponRegistry.get(weaponId) ?? null;

    if (newWeapon && newWeapon !== previousWeapon) {
      this.callbacks?.playSelectSound();
    }

    this.selectedWeapon = newWeapon;
    this.callbacks?.onWeaponChanged(newWeapon, previousWeapon);

    const wormInfo = this.callbacks?.getCurrentWormInfo();
    if (this.selectedWeapon && wormInfo) {
      this.eventBus?.emit('weapon:selected', {
        weaponId: this.selectedWeapon.id,
        teamId: wormInfo.teamId,
        wormId: wormInfo.wormId,
      });
    }

    if (this.selectedWeapon && this.requiresTargeting(this.selectedWeapon)) {
      this.callbacks?.onRequiresTargeting(this.selectedWeapon);
    }
  }

  clear(): void {
    const previousWeapon = this.selectedWeapon;
    this.selectedWeapon = null;
    this.callbacks?.onWeaponChanged(null, previousWeapon);
  }

  getSelectedWeapon(): WeaponDefinition | null {
    return this.selectedWeapon;
  }

  hasWeaponSelected(): boolean {
    return this.selectedWeapon !== null;
  }

  getSelectedWeaponId(): string | null {
    return this.selectedWeapon?.id ?? null;
  }

  isWeaponSelected(weaponId: string): boolean {
    return this.selectedWeapon?.id === weaponId;
  }

  requiresTargeting(weapon: WeaponDefinition): boolean {
    return (
      weapon.category === 'strike' ||
      weapon.category === 'homing' ||
      weapon.id === 'teleport' ||
      weapon.id === 'girder'
    );
  }

  isPowerCharging(): boolean {
    return this.selectedWeapon?.powerCharging ?? false;
  }

  getCategory(): string | null {
    return this.selectedWeapon?.category ?? null;
  }

  getMaxUsesPerTurn(): number {
    return this.selectedWeapon?.maxUsesPerTurn ?? 1;
  }

  getName(): string {
    return this.selectedWeapon?.name ?? 'None';
  }
}
